export interface SpecialtyLocationPage {
  specialtySlug: string;
  citySlug: string;
  countryCode: string;
  countryLabel: {
    fr: string;
    en: string;
  };
  specialtyLabel: {
    fr: string;
    en: string;
  };
  cityLabel: string;
  aliases: string[];
  title: {
    fr: string;
    en: string;
  };
  description: {
    fr: string;
    en: string;
  };
  intro: {
    fr: string;
    en: string;
  };
  benefits: {
    fr: string[];
    en: string[];
  };
  faqs: Array<{
    question: {
      fr: string;
      en: string;
    };
    answer: {
      fr: string;
      en: string;
    };
  }>;
  isIndexable?: boolean;
}

type LocaleText = {
  fr: string;
  en: string;
};

interface SpecialtyTemplate {
  specialtySlug: string;
  specialtyLabel: LocaleText;
  aliases: string[];
  careFocus: LocaleText;
}

interface CityTemplate {
  citySlug: string;
  cityLabel: string;
  countryCode: string;
  countryLabel: LocaleText;
}

const commonBenefits = {
  fr: [
    "Trouvez des praticiens vérifiés selon leur spécialité et leur disponibilité.",
    "Choisissez entre téléconsultation et soins à domicile lorsque ces modes sont proposés.",
    "Comparez les langues parlées, l'expérience et les services avant de prendre rendez-vous.",
  ],
  en: [
    "Find verified practitioners by specialty and availability.",
    "Choose online consultation or home care when those modes are available.",
    "Compare languages, experience, and services before booking.",
  ],
};

const getFaqs = (specialtyFr: string, specialtyEn: string, city = "Abidjan") => [
  {
    question: {
      fr: `Comment prendre rendez-vous avec un ${specialtyFr.toLowerCase()} à ${city} ?`,
      en: `How do I book a ${specialtyEn.toLowerCase()} in ${city}?`,
    },
    answer: {
      fr: `Sélectionnez un praticien, choisissez le service disponible, puis indiquez la date, l'heure et vos informations de contact pour confirmer la demande de rendez-vous.`,
      en: `Choose a practitioner, select an available service, then provide the appointment date, time, and contact details to confirm your request.`,
    },
  },
  {
    question: {
      fr: `La téléconsultation est-elle disponible avec un ${specialtyFr.toLowerCase()} ?`,
      en: `Is online consultation available with a ${specialtyEn.toLowerCase()}?`,
    },
    answer: {
      fr: "La disponibilité dépend du praticien et du service choisi. Les profils indiquent les modes de consultation proposés.",
      en: "Availability depends on the practitioner and selected service. Each profile shows the consultation modes offered.",
    },
  },
  {
    question: {
      fr: "Puis-je choisir un praticien selon la langue parlée ?",
      en: "Can I choose a practitioner by language?",
    },
    answer: {
      fr: "Oui. Les profils affichent les langues parlées afin de vous aider à choisir le praticien le plus adapté.",
      en: "Yes. Profiles show spoken languages to help you choose the practitioner that best fits your needs.",
    },
  },
];

const abidjanSpecialties: SpecialtyTemplate[] = [
  {
    specialtySlug: "gynecologue",
    specialtyLabel: { fr: "Gynécologue", en: "Gynecologist" },
    aliases: ["gynecologue", "gynécologue", "gynecology", "gynecologist", "gynécologie"],
    careFocus: {
      fr: "le suivi gynécologique, la fertilité, la grossesse, la ménopause et les questions de santé féminine",
      en: "women's health, fertility, pregnancy, menopause, and related care needs",
    },
  },
  {
    specialtySlug: "medecin-generaliste",
    specialtyLabel: { fr: "Médecin généraliste", en: "General practitioner" },
    aliases: ["medecin generaliste", "médecin généraliste", "general practitioner", "generaliste", "généraliste"],
    careFocus: {
      fr: "une première évaluation, un suivi médical ou une orientation vers un spécialiste",
      en: "first-line care, medical follow-up, or referral to a specialist",
    },
  },
  {
    specialtySlug: "dermatologue",
    specialtyLabel: { fr: "Dermatologue", en: "Dermatologist" },
    aliases: ["dermatologue", "dermatologist", "dermatology", "dermatologie"],
    careFocus: {
      fr: "les problèmes de peau, d'acné, d'allergies cutanées, de cheveux ou d'ongles",
      en: "skin concerns, acne, allergies, hair, or nail issues",
    },
  },
  {
    specialtySlug: "pediatre",
    specialtyLabel: { fr: "Pédiatre", en: "Pediatrician" },
    aliases: ["pediatre", "pédiatre", "pediatrician", "pediatrics", "pédiatrie"],
    careFocus: {
      fr: "le suivi, les symptômes courants et les besoins de santé de votre enfant",
      en: "follow-up, common symptoms, and children's health needs",
    },
  },
  {
    specialtySlug: "sage-femme",
    specialtyLabel: { fr: "Sage-femme", en: "Midwife" },
    aliases: ["sage femme", "sage-femme", "midwife", "midwifery"],
    careFocus: {
      fr: "le suivi de grossesse, la préparation à la naissance et l'accompagnement maternel",
      en: "pregnancy follow-up, birth preparation, and maternity support",
    },
  },
  {
    specialtySlug: "psychologue",
    specialtyLabel: { fr: "Psychologue", en: "Psychologist" },
    aliases: ["psychologue", "psychologist", "psychology", "psychologie"],
    careFocus: {
      fr: "un accompagnement lié au stress, à l'anxiété, au couple ou au bien-être mental",
      en: "support with stress, anxiety, relationships, or mental wellbeing",
    },
  },
  {
    specialtySlug: "nutritionniste",
    specialtyLabel: { fr: "Nutritionniste", en: "Nutritionist" },
    aliases: ["nutritionniste", "nutritionist", "nutrition", "diététique", "dietetique"],
    careFocus: {
      fr: "améliorer votre alimentation, gérer votre poids ou adapter votre nutrition à votre état de santé",
      en: "dietary guidance, metabolic health, or weight management",
    },
  },
  {
    specialtySlug: "oncologue",
    specialtyLabel: { fr: "Oncologue", en: "Oncologist" },
    aliases: ["oncologue", "oncologist", "oncology", "oncologie", "cancer"],
    careFocus: {
      fr: "un avis spécialisé, une orientation ou un suivi lié au cancer",
      en: "specialist advice, referral, or cancer-related follow-up",
    },
  },
];

const expansionSpecialties = abidjanSpecialties.filter(specialty =>
  ["dermatologue", "gynecologue", "pediatre", "medecin-generaliste"].includes(
    specialty.specialtySlug
  )
);

const cities: CityTemplate[] = [
  {
    citySlug: "abidjan",
    cityLabel: "Abidjan",
    countryCode: "CI",
    countryLabel: { fr: "Côte d'Ivoire", en: "Côte d'Ivoire" },
  },
  {
    citySlug: "dakar",
    cityLabel: "Dakar",
    countryCode: "SN",
    countryLabel: { fr: "Sénégal", en: "Senegal" },
  },
  {
    citySlug: "douala",
    cityLabel: "Douala",
    countryCode: "CM",
    countryLabel: { fr: "Cameroun", en: "Cameroon" },
  },
  {
    citySlug: "yaounde",
    cityLabel: "Yaoundé",
    countryCode: "CM",
    countryLabel: { fr: "Cameroun", en: "Cameroon" },
  },
];

const buildSpecialtyLocationPage = (
  specialty: SpecialtyTemplate,
  city: CityTemplate
): SpecialtyLocationPage => ({
  specialtySlug: specialty.specialtySlug,
  citySlug: city.citySlug,
  countryCode: city.countryCode,
  countryLabel: city.countryLabel,
  specialtyLabel: specialty.specialtyLabel,
  cityLabel: city.cityLabel,
  aliases: specialty.aliases,
  title: {
    fr: `${specialty.specialtyLabel.fr} à ${city.cityLabel} | Rendez-vous en ligne | La Ruche Health`,
    en: `${specialty.specialtyLabel.en} in ${city.cityLabel} | Online Booking | La Ruche Health`,
  },
  description: {
    fr: `Trouvez un ${specialty.specialtyLabel.fr.toLowerCase()} à ${city.cityLabel} et prenez rendez-vous en ligne avec La Ruche Health.`,
    en: `Find a ${specialty.specialtyLabel.en.toLowerCase()} in ${city.cityLabel} and book online with La Ruche Health.`,
  },
  intro: {
    fr: `Prenez rendez-vous avec un ${specialty.specialtyLabel.fr.toLowerCase()} à ${city.cityLabel} pour ${specialty.careFocus.fr}.`,
    en: `Book a ${specialty.specialtyLabel.en.toLowerCase()} in ${city.cityLabel} for ${specialty.careFocus.en}.`,
  },
  benefits: commonBenefits,
  faqs: getFaqs(
    specialty.specialtyLabel.fr,
    specialty.specialtyLabel.en,
    city.cityLabel
  ),
});

export const specialtyLocationPages: SpecialtyLocationPage[] = cities.flatMap(
  city =>
    (city.citySlug === "abidjan" ? abidjanSpecialties : expansionSpecialties).map(
      specialty => buildSpecialtyLocationPage(specialty, city)
    )
);

export const getSpecialtyLocationPage = (
  specialtySlug: string,
  citySlug: string
) =>
  specialtyLocationPages.find(
    page => page.specialtySlug === specialtySlug && page.citySlug === citySlug
  ) ?? null;
